import { gql } from '@apollo/client';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Box, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { Table } from '../components/microLayouts/table';

export function Jobs() {
  const navigate = useNavigate();
  const { data, pagination, loading, refetch } = useOffsetPaginatedQuery(query, {
    variables: page => ({ input: { page } }),
    total: data => data.sqsJobMany.total
  });

  const items = data?.sqsJobMany.items ?? [];
  const hasInflight = items.some(job => job.status === 'pending' || job.status === 'processing');

  useEffect(() => {
    if (!hasInflight) return;
    const interval = setInterval(() => {
      void refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [hasInflight, refetch]);

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Background jobs"
        primaryAction={
          <Button variant="secondary" onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      <Table
        items={loading ? undefined : items}
        pagination={pagination}
        rowKey={job => job.id}
        rowProps={job => ({
          onClick: () => navigate(`/jobs/${job.id}`),
          sx: { cursor: 'pointer' }
        })}
        columns={[
          {
            label: 'Type',
            render: job => <Typography variant="body2">{job.type}</Typography>
          },
          {
            label: 'Owner',
            render: job =>
              job.accountProvision != null ? (
                <Typography variant="body2">
                  {`${job.accountProvision.ownerFirstName} ${job.accountProvision.ownerLastName}`}
                </Typography>
              ) : (
                <Typography variant="body2">—</Typography>
              )
          },
          {
            label: 'Email',
            render: job => (
              <Typography variant="body2">
                {job.accountProvision?.ownerEmail ??
                  job.accountDeletion?.ownerEmail ??
                  job.accountRestore?.ownerEmail ??
                  '—'}
              </Typography>
            )
          },
          {
            label: 'Organisation',
            render: job => (
              <Typography variant="body2">
                {job.accountProvision?.organisationName ??
                  job.accountDeletion?.accountAlias ??
                  job.accountRestore?.accountAlias ??
                  '—'}
              </Typography>
            )
          },
          {
            label: 'Account ID',
            render: job => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {job.accountProvision?.accountId ??
                  job.accountDeletion?.accountId ??
                  job.accountRestore?.accountId ??
                  '—'}
              </Typography>
            )
          },
          {
            label: 'Status',
            render: job => (
              <Stack direction="row" alignItems="center" gap={1}>
                {(job.status === 'pending' || job.status === 'processing') && (
                  <CircularProgress size={12} />
                )}
                <Typography variant="body2">{job.status}</Typography>
              </Stack>
            )
          },
          {
            label: 'Progress',
            render: job => (
              <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 220 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={job.progress}
                    color={
                      job.status === 'failed'
                        ? 'error'
                        : job.status === 'completed'
                          ? 'success'
                          : 'primary'
                    }
                  />
                </Box>
                <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right' }}>
                  {job.progress}%
                </Typography>
              </Stack>
            )
          },
          {
            label: 'Created at',
            render: job => (
              <Typography variant="body2">{new Date(job.createdAt).toLocaleString()}</Typography>
            )
          },
          {
            label: 'Completed at',
            render: job => (
              <Typography variant="body2">
                {job.completedAt != null ? new Date(job.completedAt).toLocaleString() : '—'}
              </Typography>
            )
          },
          {
            label: 'Created by',
            render: job => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {job.createdBy ?? '—'}
              </Typography>
            )
          },
          {
            label: 'Error',
            render: job => (
              <Typography
                variant="body2"
                sx={{ color: 'error.main', maxWidth: 320, overflowWrap: 'anywhere' }}
              >
                {job.errorMessage ?? '—'}
              </Typography>
            )
          }
        ]}
        noDataPlaceholder="No jobs yet."
      />
    </MainWrapper>
  );
}

const query = gql<SqsJobManyGQL>`
  query SqsJobMany($input: SqsJobManyInput) {
    sqsJobMany(input: $input) {
      total
      items {
        id
        type
        status
        progress
        errorMessage
        createdBy
        createdAt
        completedAt
        accountProvision {
          ownerFirstName
          ownerLastName
          ownerEmail
          organisationName
          accountId
        }
        accountDeletion {
          accountId
          accountAlias
          ownerEmail
          userCount
        }
        accountRestore {
          accountId
          accountAlias
          ownerEmail
        }
      }
    }
  }
`;
